import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Star, Upload, Loader2, Check, X, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSeo } from "../lib/useSeo";
import { resolveMediaUrl } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 3;

export default function ReviewSubmit() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [photos, setPhotos] = useState([]);   // uploaded URLs
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useSeo({
    title: "Laisser un avis · MAISON DOUBA",
    description: "Partagez votre expérience sur votre parfum MAISON DOUBA.",
    canonical: `https://wwwmaisondouba.com/avis/${token}`,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/context/${token}`);
        setCtx(data);
        if (data.customer_name) setName(data.customer_name);
      } catch (e) {
        setError(e.response?.data?.detail || "Lien invalide.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleUpload = async (files) => {
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos.`);
      return;
    }
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/reviews/upload-photo/${token}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newUrls.push(data.url);
      }
      setPhotos((prev) => [...prev, ...newUrls]);
      toast.success("Photo ajoutée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload impossible");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) {
      toast.error("Attribuez une note en étoiles");
      return;
    }
    if (body.trim().length < 10) {
      toast.error("Merci d'écrire quelques mots (au moins 10 caractères)");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/submit/${token}`, {
        rating,
        title: title.trim(),
        body: body.trim(),
        customer_name: name.trim(),
      });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-[color:var(--gold)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-40 pb-24 px-6 max-w-md mx-auto text-center" data-testid="review-error">
          <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-6">Avis indisponible</p>
          <h1 className="font-serif text-4xl mb-4">Lien invalide</h1>
          <p className="text-white/60 text-sm mb-10">{error}</p>
          <Link to="/" className="btn-outline">Retour à l'accueil</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-40 pb-24 px-6 max-w-lg mx-auto text-center" data-testid="review-thanks">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[color:var(--gold)]/10 border border-[color:var(--gold)] mb-8">
            <Check className="text-[color:var(--gold)]" />
          </div>
          <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-4">Merci</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-6">Votre avis a été reçu.</h1>
          <p className="text-white/60 leading-relaxed">
            Il sera publié sur la fiche produit après une rapide validation par notre équipe (généralement sous 24h).
            Marqué comme <em className="text-[color:var(--gold)] not-italic">« Achat vérifié »</em>, il aidera d'autres clients à choisir en confiance.
          </p>
          <Link to="/" className="btn-outline mt-10 inline-flex">Retour à la boutique <ArrowRight size={14} /></Link>
        </div>
        <Footer />
      </div>
    );
  }

  const product = ctx?.product || {};

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="review-submit-page">
      <Navbar />
      <div className="pt-40 pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <p className="label-caps text-[color:var(--gold)] text-[10px] tracking-[0.3em] mb-4 text-center">Votre avis · Achat vérifié</p>
          <h1 className="font-serif text-4xl md:text-5xl text-center mb-4">Un mot sur votre parfum ?</h1>
          <p className="text-white/50 text-sm text-center mb-12">Commande n°#{ctx.order_short}</p>

          {/* Product card */}
          {product.name && (
            <div className="flex items-center gap-5 border border-white/10 p-5 mb-10">
              {product.image_main && (
                <img src={resolveMediaUrl(product.image_main)} alt={product.name} className="w-24 h-24 object-cover" />
              )}
              <div className="flex-1">
                <p className="label-caps text-[10px] text-white/40 tracking-[0.25em]">{product.brand}</p>
                <p className="font-serif text-2xl mt-1">{product.name}</p>
              </div>
              <span className="inline-flex items-center gap-1.5 label-caps text-[9px] text-[color:var(--gold)] border border-[color:var(--gold)] px-3 py-1.5">
                <ShieldCheck size={12} /> Vérifié
              </span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-10">
            {/* Rating stars */}
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-4">Votre note</label>
              <div className="flex items-center gap-2" data-testid="rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`star-${n}`}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    className="p-2 transition-transform hover:scale-110"
                    aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                  >
                    <Star
                      size={36}
                      strokeWidth={1.5}
                      className={
                        (hover || rating) >= n
                          ? "text-[color:var(--gold)] fill-[color:var(--gold)]"
                          : "text-white/25"
                      }
                    />
                  </button>
                ))}
                {rating > 0 && (
                  <span className="ml-4 text-white/60 text-sm">{rating}/5</span>
                )}
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Votre prénom (affiché)</label>
              <input
                data-testid="review-name"
                required
                maxLength={80}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex : Isabelle"
                className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--gold)] py-3 outline-none"
              />
            </div>

            {/* Title */}
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Un titre (optionnel)</label>
              <input
                data-testid="review-title"
                maxLength={120}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex : Un sillage envoûtant"
                className="w-full bg-transparent border-b border-white/20 focus:border-[color:var(--gold)] py-3 outline-none"
              />
            </div>

            {/* Body */}
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-2">Votre commentaire</label>
              <textarea
                data-testid="review-body"
                required
                minLength={10}
                maxLength={2000}
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Racontez votre expérience : les notes qui vous ont marqué, la tenue, l'ambiance…"
                className="w-full bg-transparent border border-white/15 focus:border-[color:var(--gold)] px-4 py-3 outline-none resize-none text-sm leading-relaxed"
              />
              <p className="text-right text-[10px] text-white/40 mt-1">{body.length}/2000</p>
            </div>

            {/* Photos */}
            <div>
              <label className="label-caps text-[10px] text-white/50 tracking-[0.25em] block mb-3">Photos (optionnel — max {MAX_PHOTOS})</label>
              <div className="flex flex-wrap gap-3">
                {photos.map((url) => (
                  <div key={url} className="relative w-24 h-24 border border-white/20 overflow-hidden group">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${url}`} alt="preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setPhotos((prev) => prev.filter((u) => u !== url))}
                      className="absolute top-1 right-1 bg-black/70 border border-white/30 p-1 opacity-0 group-hover:opacity-100"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {photos.length < MAX_PHOTOS && (
                  <button
                    type="button"
                    data-testid="upload-photo-btn"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-24 h-24 border border-dashed border-white/30 hover:border-[color:var(--gold)] flex flex-col items-center justify-center text-white/40 hover:text-[color:var(--gold)] transition-colors"
                  >
                    {uploading ? <Loader2 size={20} className="animate-spin" /> : <><Upload size={20} /><span className="text-[9px] mt-1">Ajouter</span></>}
                  </button>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                multiple
                hidden
                onChange={(e) => handleUpload(Array.from(e.target.files || []))}
              />
              <p className="text-[10px] text-white/40 mt-2">JPG, PNG ou WebP — max 5 MB par photo.</p>
            </div>

            <button
              type="submit"
              data-testid="review-submit-btn"
              disabled={submitting || rating < 1}
              className="w-full bg-[color:var(--gold)] text-black py-4 label-caps text-xs tracking-[0.3em] flex items-center justify-center gap-2 hover:bg-white transition-colors disabled:opacity-50"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <>Envoyer mon avis <ArrowRight size={14} /></>}
            </button>

            <p className="text-white/40 text-[11px] text-center leading-relaxed">
              Votre avis sera publié après modération. En soumettant vous acceptez notre{" "}
              <Link to="/legal/confidentialite" className="text-[color:var(--gold)] hover:underline">politique de confidentialité</Link>.
            </p>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
